import type { UtilityContext } from '@change-corgi/core/react/utilityContext';

import type { SessionUser } from 'src/shared/session';

import { VANITY_ERROR_MAP } from '../../../../../constants';

import { StarterDashboardCreateVanityUrl } from './saveVanityUrl.graphql';
import type {
	StarterDashboardCreateVanityUrlMutation,
	StarterDashboardCreateVanityUrlMutationVariables,
} from './saveVanityUrl.graphql';

type SaveVanityUrlReturn = { status: 'SUCCESS'; vanityUrl: string };
type SaveVanityUrlParams = {
	url: string;
	petitionSlug: string;
	petitionId: string;
	user?: SessionUser;
	uuid: string;
};
// eslint-disable-next-line max-lines-per-function, max-statements
export async function saveVanityUrl(
	{ url, petitionSlug, user, petitionId, uuid }: SaveVanityUrlParams,
	{ gql: { fetch }, errorReporter, resolveUrl }: UtilityContext,
): Promise<SaveVanityUrlReturn> {
	if (!user) {
		const err = new Error(VANITY_ERROR_MAP.UNAUTHENTICATED.code, {
			cause: new Error('user unauthenticated in save vanity url'),
		});
		void errorReporter.report({
			error: new Error(`Failed to create starter vanity url (${VANITY_ERROR_MAP.UNAUTHENTICATED.code})`),
			params: {
				vanity: url,
				petitionSlug,
			},
		});
		throw err;
	}

	const input = {
		customExpression: url,
		destinationUrl: resolveUrl(
			`/p/${petitionSlug}?utm_medium=custom_url&utm_source=share_petition&recruited_by_id=${uuid}`,
		),
		petitionId,
	};

	const { data, errors, requestId } = await fetch<
		StarterDashboardCreateVanityUrlMutation,
		StarterDashboardCreateVanityUrlMutationVariables
	>({
		query: StarterDashboardCreateVanityUrl,
		variables: { input },
		rejectOnError: false,
	});

	const reportError = (code: string) => {
		void errorReporter.report({
			error: new Error(`Failed to create starter vanity url (${code})`),
			params: {
				requestId,
				vanity: url,
				petitionSlug,
				starterVanityUrlErrorMessage: data?.createStarterVanityUrl?.starterVanityUrlErrorMessage,
				customExpressionErrorMessage: data?.createStarterVanityUrl?.customExpressionErrorMessage,
				destinationUrlErrorMessage: data?.createStarterVanityUrl?.destinationUrlErrorMessage,
				petitionErrorMessage: data?.createStarterVanityUrl?.petitionErrorMessage,
			},
		});
	};

	if (errors || !data || !data.createStarterVanityUrl) {
		const err = new Error(VANITY_ERROR_MAP.GENERIC_ERROR.code);
		reportError(err.message);
		throw err;
	}

	if (data.createStarterVanityUrl.customExpressionErrorMessage === 'invalid_format') {
		const err = new Error(VANITY_ERROR_MAP.INVALID_URL.code);
		reportError(err.message);
		throw err;
	}

	if (data.createStarterVanityUrl.customExpressionErrorMessage === 'banned_word') {
		const err = new Error(VANITY_ERROR_MAP.BANNED_WORD.code);
		reportError(err.message);
		throw err;
	}

	if (data.createStarterVanityUrl.customExpressionErrorMessage === 'not_unique') {
		const err = new Error(VANITY_ERROR_MAP.ALREADY_EXISTS.code);
		reportError(err.message);
		throw err;
	}

	if (!data.createStarterVanityUrl.success || !data.createStarterVanityUrl.petition?.starterVanityUrl) {
		const err = new Error(VANITY_ERROR_MAP.GENERIC_ERROR.code);
		reportError(err.message);
		throw err;
	}
	return {
		status: 'SUCCESS',
		vanityUrl: data.createStarterVanityUrl.petition.starterVanityUrl,
	};
}
